import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  ShoppingCart,
  Wallet,
  Users,
  Loader2,
  Play,
  CheckCircle2,
  XCircle,
  Coins,
  Settings,
  Search
} from 'lucide-react';

import { getApiUrl, getAuthenticatedAxios } from '../../utils/api';

const API_URL = getApiUrl();

const BlumGroupBuy = ({ tokens = [], selectedToken, onTokenSelect }) => {
  // Wallet/Group selection
  const [groups, setGroups] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedWallets, setSelectedWallets] = useState([]);
  const [loadingWallets, setLoadingWallets] = useState(false);
  
  // Settings
  const [amountTon, setAmountTon] = useState(0.1);
  const [slippage, setSlippage] = useState(3);
  const [delayMin, setDelayMin] = useState(500);
  const [delayMax, setDelayMax] = useState(2000);
  
  // Token search
  const [tokenSearch, setTokenSearch] = useState('');
  
  // Execution
  const [executing, setExecuting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    loadGroups();
    loadAllWallets();
  }, []);

  const loadGroups = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/wallets/groups`);
      if (response.data.groups) {
        setGroups(response.data.groups || []);
      }
    } catch (err) {
      console.error('Error loading groups:', err);
    }
  };

  const loadAllWallets = async () => {
    setLoadingWallets(true);
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get(`${API_URL}/api/wallets/list`);
      if (response.data.wallets) {
        const activeWallets = response.data.wallets.filter(w => 
          parseFloat(w.ton_balance || 0) / 1e9 > 0.05
        );
        setWallets(activeWallets);
      }
    } catch (err) {
      console.error('Error loading wallets:', err);
    } finally {
      setLoadingWallets(false);
    }
  };

  const handleGroupChange = (value) => {
    setSelectedGroup(value);
    if (value === 'all') {
      setSelectedWallets(wallets.map(w => w.address || w._id));
    } else if (value) {
      // Filter wallets by group_name
      const groupWallets = wallets.filter(w => w.group_name === value);
      setSelectedWallets(groupWallets.map(w => w.address || w._id));
    } else {
      setSelectedWallets([]);
    }
  };

  const toggleWallet = (walletId) => {
    setSelectedWallets(prev => 
      prev.includes(walletId) 
        ? prev.filter(id => id !== walletId)
        : [...prev, walletId]
    );
    setSelectedGroup('');
  };

  const executeGroupBuy = async () => {
    if (!selectedToken) {
      toast.error('Выберите токен');
      return;
    }
    if (selectedWallets.length === 0) {
      toast.error('Выберите кошельки');
      return;
    }

    setExecuting(true);
    setResults(null);

    try {
      const api = getAuthenticatedAxios();
      const response = await api.post(`${API_URL}/api/blum/trade/group-buy`, {
        token_address: selectedToken.master_address,
        wallet_ids: selectedWallets,
        amount_ton: amountTon,
        slippage_percent: slippage,
        delay_min_ms: delayMin,
        delay_max_ms: delayMax
      });

      setResults(response.data);

      if (response.data.success) {
        toast.success(`Куплено: ${response.data.successful_buys}/${response.data.total_wallets}`);
      } else {
        toast.error('Ошибка выполнения');
      }
    } catch (err) {
      console.error('Error executing group buy:', err);
      toast.error('Ошибка: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExecuting(false);
    }
  };

  // Filter tokens by search
  const filteredTokens = tokens.filter(t => 
    !tokenSearch || 
    t.symbol?.toLowerCase().includes(tokenSearch.toLowerCase()) ||
    t.name?.toLowerCase().includes(tokenSearch.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Token Selection */}
      <Card className="bg-gradient-to-br from-indigo-900/50 to-violet-900/50 border-indigo-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2">
            <Coins className="h-5 w-5" />
            Выбор токена
          </CardTitle>
          <CardDescription className="text-indigo-200">
            Выберите токен для групповой покупки
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-indigo-300" />
            <Input
              placeholder="Поиск по символу или названию..."
              value={tokenSearch}
              onChange={(e) => setTokenSearch(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-indigo-300/50"
            />
          </div>

          {/* Token list */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
            {filteredTokens.slice(0, 50).map(token => (
              <button
                key={token.master_address}
                onClick={() => onTokenSelect?.(token)}
                className={`p-2 rounded-lg text-left text-xs transition-all ${
                  selectedToken?.master_address === token.master_address
                    ? 'bg-indigo-500/40 border border-indigo-400'
                    : 'bg-white/5 border border-transparent hover:bg-white/10'
                }`}
              >
                <div className="text-white font-medium truncate">
                  {token.symbol || 'UNKNOWN'}
                </div>
                <div className="text-indigo-300 truncate text-[10px]">
                  {token.name?.slice(0, 15)}
                </div>
                <div className="text-indigo-400 text-[10px]">
                  {token.ton_liq_collected?.toFixed(1) || 0} TON
                </div>
              </button>
            ))}
          </div>

          {/* Selected token info */}
          {selectedToken && (
            <div className="mt-3 p-3 bg-indigo-500/20 rounded-lg border border-indigo-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-white font-medium">{selectedToken.symbol}</span>
                  <span className="text-indigo-300 text-sm ml-2">{selectedToken.name}</span>
                </div>
                <Badge className={selectedToken.trading_enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                  {selectedToken.trading_enabled ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              <div className="text-indigo-300 text-xs mt-1">
                Ликвидность: {selectedToken.ton_liq_collected?.toFixed(2) || 0} TON
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Wallet Selection */}
      <Card className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 border-purple-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="h-5 w-5" />
            Выбор кошельков
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Group selector */}
          <div>
            <Label className="text-purple-200 text-sm">Группа кошельков</Label>
            <Select value={selectedGroup} onValueChange={handleGroupChange}>
              <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
                <SelectValue placeholder="Выберите группу..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все кошельки ({wallets.length})</SelectItem>
                {groups.map(group => (
                  <SelectItem key={group.group_name} value={group.group_name}>
                    {group.group_name} ({group.count || 0})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Individual wallets */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-purple-200 text-sm">
                Выбрано: {selectedWallets.length} кошельков
              </Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedWallets([])}
                className="text-purple-300 hover:text-white h-6"
              >
                Очистить
              </Button>
            </div>
            
            {loadingWallets ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-40 overflow-y-auto">
                {wallets.map(wallet => (
                  <button
                    key={wallet.address || wallet._id}
                    onClick={() => toggleWallet(wallet.address || wallet._id)}
                    className={`p-2 rounded-lg text-left text-xs transition-all ${
                      selectedWallets.includes(wallet.address || wallet._id)
                        ? 'bg-purple-500/40 border border-purple-400'
                        : 'bg-white/5 border border-transparent hover:bg-white/10'
                    }`}
                  >
                    <div className="text-white font-medium truncate">
                      {wallet.group_name || wallet.name || 'Wallet'} #{wallet.wallet_index || 1}
                    </div>
                    <div className="text-purple-300 truncate text-[10px]">
                      {(parseFloat(wallet.ton_balance || 0) / 1e9).toFixed(2)} TON
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Settings & Execute */}
      <Card className="bg-gradient-to-br from-green-900/50 to-emerald-900/50 border-green-500/30">
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-green-200 text-sm">Сумма TON</Label>
              <Input
                type="number"
                value={amountTon}
                onChange={(e) => setAmountTon(parseFloat(e.target.value) || 0.1)}
                step="0.1"
                min="0.1"
                className="bg-white/10 border-white/20 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-green-200 text-sm">Slippage %</Label>
              <Input
                type="number"
                value={slippage}
                onChange={(e) => setSlippage(parseFloat(e.target.value) || 3)}
                step="0.5"
                min="0.5"
                max="10"
                className="bg-white/10 border-white/20 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-green-200 text-sm">Мин. задержка (мс)</Label>
              <Input
                type="number"
                value={delayMin}
                onChange={(e) => setDelayMin(parseInt(e.target.value) || 500)}
                className="bg-white/10 border-white/20 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-green-200 text-sm">Макс. задержка (мс)</Label>
              <Input
                type="number"
                value={delayMax}
                onChange={(e) => setDelayMax(parseInt(e.target.value) || 2000)}
                className="bg-white/10 border-white/20 text-white mt-1"
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <div>
              <div className="text-white font-medium">
                {selectedToken ? `Купить ${selectedToken.symbol}` : 'Выберите токен'}
              </div>
              <div className="text-green-300 text-sm">
                {selectedWallets.length} × {amountTon} TON = {(selectedWallets.length * amountTon).toFixed(2)} TON
              </div>
            </div>
            <Button
              onClick={executeGroupBuy}
              disabled={executing || !selectedToken || selectedWallets.length === 0}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6"
            >
              {executing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Выполняется...
                </>
              ) : (
                <>
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Купить для всех
                </>
              )}
            </Button>
          </div>

          {/* Results */}
          {results && (
            <div className="bg-black/30 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white font-medium">Результаты</span>
                <div className="flex gap-2">
                  <Badge className="bg-green-500/20 text-green-400">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    {results.successful_buys}
                  </Badge>
                  <Badge className="bg-red-500/20 text-red-400">
                    <XCircle className="h-3 w-3 mr-1" />
                    {results.failed_buys}
                  </Badge>
                </div>
              </div>

              <div className="space-y-2 max-h-48 overflow-y-auto">
                {results.purchases?.map((p, i) => (
                  <div 
                    key={i}
                    className={`flex items-center justify-between p-2 rounded ${
                      p.status === 'success' 
                        ? 'bg-green-500/10 border border-green-500/30' 
                        : 'bg-red-500/10 border border-red-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {p.status === 'success' ? (
                        <CheckCircle2 className="h-4 w-4 text-green-400" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-400" />
                      )}
                      <span className="text-white text-sm">{p.wallet_name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-white text-sm">{p.amount_ton} TON</div>
                      {p.error && (
                        <div className="text-red-400 text-xs truncate max-w-32">{p.error}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BlumGroupBuy;

